import { Page } from 'components/Page';
import React from 'react';
import { View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { EngageLoader } from './EngageLoader';
import { ScheduledVisitsHeader } from './ScheduledVisitsHeader';
import { PageEmpty } from 'components/PageEmpty';
import { EngageScheduledVisitListItem } from 'types/Engage';
import { Grid, GridProps } from 'components/Grid/Grid';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEngageIsLoading,
  selectEngageScheduledVisitItem,
  selectEngageScheduledVisitsIds,
} from 'store/remote/engageSelectors';
import { doEngageScheduledVisitsLoad } from 'store/remote/engageActions';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import { capitalize } from 'lodash';
import { isoDateStringToDate, formatDateRange } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { NAVIGATE_MODAL } from 'store/app/actions';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.Engage],
  Screens.EngageScheduledVisits
>;

type DefinedGridProps = GridProps<
  'dates' | 'status',
  string,
  {
    pageKey: string;
    onRowPress: (scheduledVisit: EngageScheduledVisitListItem) => void;
  }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const scheduledVisit = useSelector((state) =>
    selectEngageScheduledVisitItem(state, extraData.pageKey, id),
  );

  if (!scheduledVisit) {
    return null;
  }

  const getDateDisplayText = (): string => {
    if (!scheduledVisit.startDateTime || !scheduledVisit.endDateTime) {
      return 'No Dates';
    }
    const startDate = isoDateStringToDate(scheduledVisit.startDateTime);
    const endDate = isoDateStringToDate(scheduledVisit.endDateTime);
    return formatDateRange(startDate, endDate, 'short');
  };

  const daySlotCount = scheduledVisit.slots.length;
  const daySlotCountText = pluralise(daySlotCount, '{x} Day', '{x} Days');

  return {
    onPress: () => {
      extraData.onRowPress(scheduledVisit);
    },
    columns: {
      dates: () => (
        <View>
          <Txt weight="bold">{getDateDisplayText()}</Txt>
          <Txt size="xsmall">{daySlotCountText}</Txt>
        </View>
      ),
      status: () => {
        return (
          <View>
            <Txt size="small">{capitalize(scheduledVisit.status)}</Txt>
            {!!scheduledVisit.historic && (
              <Txt size="xsmall" style={{ color: theme.colors.orange }}>
                Historic
              </Txt>
            )}
          </View>
        );
      },
    },
  };
};

export const ScheduledVisits: React.FC<Props> = ({
  navigation,
  route: {
    params: { pageKey, property },
  },
}) => {
  const dispatch = useDispatch();

  const handleOpenScheduledVisitModal = React.useCallback(
    (scheduledVisit?: EngageScheduledVisitListItem) => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngageFlatDoorScheduledVisit,
          size: 'large',
          params: {
            propertyId: property.id,
            pageKey,
            scheduledVisit,
          },
        }),
      );
    },
    [dispatch, property.id, pageKey],
  );

  React.useEffect(() => {
    if (!property) {
      return;
    }

    loadInitialised.current = true;
    dispatch(
      doEngageScheduledVisitsLoad({
        key: pageKey,
        propertyId: property.id,
      }),
    );
  }, [dispatch, property, pageKey]);

  const handlePressBack = () => {
    navigation.pop();
  };

  const handleRowPress = (scheduledVisit: EngageScheduledVisitListItem) => {
    handleOpenScheduledVisitModal(scheduledVisit);
  };

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Dates',
      dataIndex: 'dates',
      minWidth: 220,
      flex: 1,
    },
    {
      name: 'Status',
      dataIndex: 'status',
      width: 95,
    },
  ]);

  const gridIds = useSelector((state) =>
    selectEngageScheduledVisitsIds(state, pageKey),
  );
  const isLoading = useSelector((state) =>
    selectEngageIsLoading(state, pageKey),
  );

  const loadInitialised = React.useRef(!!gridIds?.length);

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      <EngageLoader pageKey={pageKey} />
      <Page
        isInModal={false}
        contentPaddingTop={0}
        ignoreSafeAreaBottom
        header={
          <ScheduledVisitsHeader
            onBackPress={handlePressBack}
            property={property}
            pageKey={pageKey}
            scheduledVisitsCount={gridIds.length}
            isLoading={isLoading}
          />
        }
      >
        <Page isInModal={false} contentPaddingTop={0} ignoreSafeAreaBottom>
          {gridIds.length > 0 && (
            <Grid
              columns={columns}
              data={gridIds}
              extraData={{
                pageKey,
                onRowPress: handleRowPress,
              }}
              getRowData={useGetRowData}
              keyExtractor={(i) => `scheduled-visit-${i}`}
            />
          )}
          {!loadInitialised.current && (
            <PageEmpty
              text="Waiting..."
              style={{ backgroundColor: theme.colors.background }}
            />
          )}
          {gridIds.length === 0 && !!loadInitialised.current && (
            <PageEmpty
              text="No Scheduled Visits Found"
              subtext="Try creating a scheduled visit using the button above"
              iconCircled={false}
              style={{ backgroundColor: theme.colors.background }}
            />
          )}
        </Page>
      </Page>
    </View>
  );
};
